import React, {useRef} from 'react'
import PropTypes from 'prop-types'
import {
    Box,
    Text,
    AccordionItem,
    AccordionButton,
    AccordionPanel,
    useStyleConfig,
} from '@chakra-ui/react'
import {AddIcon, MinusIcon} from '../../../icons'
import {remCalc} from '../../../../../../plugins/px-to-rem-babel-custom-plugin/utils/remCalc'
import AccordionList from './accordion-list'
import SanitizedHtml from '../../../html-sanitizer'

const AccordionItemBlock = ({
    item,
    isAccordionList,
    hasBigFontSize,
    hasSecondaryTitle,
    contrasted,
    smallGap = false,
    leadingIcon,
    styleVariant,
}) => {
    const styles = useStyleConfig('Accordion', {variant: styleVariant})
    const accordionButtonRef = useRef(null)

    if (!item || Object.keys(item).length === 0) return null

    return (
        <AccordionItem
            p={0}
            overflow="hidden"
            {...(contrasted
                ? {
                      marginBottom: smallGap ? remCalc(8) : remCalc(16),
                      border: 'small',
                      borderStyle: 'solid',
                      borderColor: 'border.neutral.subtle',
                      borderRadius: 'large-1',
                  }
                : {
                      border: 'small',
                      borderStyle: 'solid',
                      borderColor: 'border.neutral.subtle',
                      borderRadius: 'large-1',
                  })}
        >
            {({isExpanded}) => (
                <Box
                    {...styles.item}
                    w="100%"
                    bg={
                        isExpanded
                            ? 'background.base.under'
                            : contrasted
                            ? 'background.light'
                            : 'transparent'
                    }
                    borderRadius="inherit"
                    onClick={(event) => {
                        if (
                            event.target.closest('[data-testid="accordion-button"]') ||
                            event.target.closest('[data-testid="accordion-content"]')
                        ) {
                            return
                        }
                        accordionButtonRef.current?.click()
                    }}
                >
                    <Box m={0} p={0} lineHeight="inherit">
                        <AccordionButton
                            ref={accordionButtonRef}
                            data-testid="accordion-button"
                            alignItems="center"
                            aria-label={`${item?.accordion_title || item?.summary} - ${
                                isExpanded ? 'expanded' : 'collapsed'
                            }`}
                        >
                            {leadingIcon}
                            <Text
                                {...styles.accordionButtonTitle}
                                {...(hasSecondaryTitle && {maxW: remCalc(200)})}
                                sx={
                                    hasBigFontSize
                                        ? styles.accordionButtonParagraphStyle
                                        : styles.accordionButtonTitleStyle
                                }
                                as="h3"
                            >
                                {item?.accordion_title || item?.summary}
                            </Text>
                            {!isExpanded && hasSecondaryTitle && (
                                <Text {...styles.accordionButtonTextDesktop}>
                                    {item?.secondary_title}
                                </Text>
                            )}
                            <Box
                                transform={isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'}
                                {...styles.iconBox}
                                aria-hidden="true"
                            >
                                {isExpanded ? (
                                    <MinusIcon {...styles.iconBox} data-testid="minus-icon" />
                                ) : (
                                    <Box {...styles.addIconBox}>
                                        <AddIcon {...styles.iconBox} data-testid="add-icon" />
                                    </Box>
                                )}
                            </Box>
                        </AccordionButton>
                    </Box>
                    {!isExpanded && hasSecondaryTitle && (
                        <Text {...styles.accordionButtonTextMobile}>{item?.secondary_title}</Text>
                    )}
                    <AccordionPanel
                        data-testid="accordion-content"
                        motionProps={{
                            transition: {
                                enter: {duration: 0.28, ease: 'easeInOut'},
                                exit: {duration: 0.28, ease: 'easeInOut'},
                            },
                        }}
                    >
                        {isAccordionList ? (
                            <AccordionList lists={item?.values} styleVariant={styleVariant} />
                        ) : (
                            <SanitizedHtml
                                as={Box}
                                html={item?.paragraph || item?.details}
                                {...styles.accordionPanelText}
                                sx={{
                                    '.videoWrapper': {
                                        position: 'relative',
                                        width: '100%',
                                        aspectRatio: '16 / 9',
                                        height: 'auto',
                                    },
                                }}
                            />
                        )}
                    </AccordionPanel>
                </Box>
            )}
        </AccordionItem>
    )
}

AccordionItemBlock.propTypes = {
    item: PropTypes.object,
    isAccordionList: PropTypes.bool,
    hasBigFontSize: PropTypes.bool,
    hasSecondaryTitle: PropTypes.bool,
    contrasted: PropTypes.bool,
    smallGap: PropTypes.bool,
    leadingIcon: PropTypes.node,
    styleVariant: PropTypes.string,
}

export default AccordionItemBlock
